import type { DateValue } from "@internationalized/date";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  BrandCreatorClassificationEnum,
  CreatorMetrics,
  CreatorMetricsSort as CreatorSortColumn,
  RuleArea,
} from "@unbox/api";
import { CREATORS_NEGATABLE_FILTERS } from "@unbox/api";
import { useCallback } from "react";
import { handleError } from "@/api/error";
import type { ChunkResponse } from "@/hooks/use-progressive-fetch";
import { toNotQuery } from "@/providers/filter-registry";
import type { Pagination } from "./pagination";
import { client, getScopedMerchantClient, useScopedMerchantClient } from "./scoped-merchant-client";

export type BrandCreatorClassification = BrandCreatorClassificationEnum;

type AudiencePercentThreshold = ">25" | ">50" | ">75";

type CreatorMetricsFilters = {
  text: string;
  riskTier?: string[];
  riskId?: string[];
  videoRiskStatus?: "no_risk" | "has_risk";
  minVideosAtRiskPercent?: number;
  maxVideosAtRiskPercent?: number;
  creatorList?: string;
  creatorListIds?: string[];
  creatorClassifications?: string[];
  includeTagIds?: string[];
  excludeTagIds?: string[];
  includeCohortIds?: string[];
  excludeCohortIds?: string[];
  creatorType?: "affiliate" | "brand";
  maxGmv?: number;
  minGmv?: number;
  productIds?: string[];
  minCtr?: number;
  maxCtr?: number;
  minCtor?: number;
  maxCtor?: number;
  minOrders?: number;
  maxOrders?: number;
  minAov?: number;
  maxAov?: number;
  classification?: BrandCreatorClassification;
  maleAudiencePercent?: AudiencePercentThreshold;
  femaleAudiencePercent?: AudiencePercentThreshold;
  age?: ("18-24" | "25-34" | "35-44" | "45-54" | "55+")[];
  toneAndApproach?: string[];
  contentFocus?: string[];
  productionQuality?: string[];
  topSellingCategories?: string[];
  minFollowers?: number;
  maxFollowers?: number;
  minTotalLikes?: number;
  maxTotalLikes?: number;
  minPostingFrequency?: number;
  maxPostingFrequency?: number;
  collaborationType?: "open" | "target";
  campaignIds?: string[];
  postedFrom?: string;
  postedTo?: string;
  creatorLevel?: string[];
  categoryPro?: string[];
  creatorLanguage?: string[];
  ruleArea?: RuleArea;
  ruleId?: string;
  not?: string[];
};

type CreatorMetricsRange = { range: { start?: DateValue; end?: DateValue } | null };
export type CreatorMetricsSortColumn = CreatorSortColumn;

type CreatorMetricsSort = {
  column: CreatorMetricsSortColumn;
  direction: "asc" | "desc";
};

const mergeCreatorListIds = (creatorListIds: string[] | undefined, creatorList: string | undefined) => {
  const merged = [...(creatorListIds ?? []), ...(creatorList ? [creatorList] : [])];
  return merged.length > 0 ? merged : undefined;
};

export const buildCreatorMetricsApiQuery = (params: CreatorMetricsFilters & CreatorMetricsRange) => {
  const { range, text, creatorList, creatorListIds } = params;
  return {
    start: range?.start?.toString() ?? "",
    end: range?.end?.toString() ?? "",
    text: text === "" ? undefined : text,
    riskTier: params.riskTier,
    riskId: params.riskId,
    videoRiskStatus: params.videoRiskStatus,
    minVideosAtRiskPercent: params.minVideosAtRiskPercent?.toString(),
    maxVideosAtRiskPercent: params.maxVideosAtRiskPercent?.toString(),
    creatorListIds: mergeCreatorListIds(creatorListIds, creatorList),
    creatorClassifications: params.creatorClassifications,
    includeTagIds: params.includeTagIds,
    excludeTagIds: params.excludeTagIds,
    includeCohortIds: params.includeCohortIds,
    excludeCohortIds: params.excludeCohortIds,
    creatorType: params.creatorType,
    maxGmv: params.maxGmv?.toString(),
    minGmv: params.minGmv?.toString(),
    productIds: params.productIds,
    minCtr: params.minCtr?.toString(),
    maxCtr: params.maxCtr?.toString(),
    minCtor: params.minCtor?.toString(),
    maxCtor: params.maxCtor?.toString(),
    minOrders: params.minOrders?.toString(),
    maxOrders: params.maxOrders?.toString(),
    minAov: params.minAov?.toString(),
    maxAov: params.maxAov?.toString(),
    classification: params.classification,
    maleAudiencePercent: params.maleAudiencePercent,
    femaleAudiencePercent: params.femaleAudiencePercent,
    age: params.age,
    toneAndApproach: params.toneAndApproach,
    contentFocus: params.contentFocus,
    productionQuality: params.productionQuality,
    topSellingCategories: params.topSellingCategories,
    minFollowers: params.minFollowers?.toString(),
    maxFollowers: params.maxFollowers?.toString(),
    minTotalLikes: params.minTotalLikes?.toString(),
    maxTotalLikes: params.maxTotalLikes?.toString(),
    minPostingFrequency: params.minPostingFrequency?.toString(),
    maxPostingFrequency: params.maxPostingFrequency?.toString(),
    collaborationType: params.collaborationType,
    campaignIds: params.campaignIds,
    creatorLevel: params.creatorLevel,
    categoryPro: params.categoryPro,
    creatorLanguage: params.creatorLanguage,
    postedFrom: params.postedFrom,
    postedTo: params.postedTo,
    ruleArea: params.ruleArea,
    ruleId: params.ruleId,
    not: toNotQuery(params.not, CREATORS_NEGATABLE_FILTERS),
  };
};

// IMPORTANT: Query keys are shared between hooks (`useCreatorsMetrics`, `useCreatorsCount`)
// and the progressive-fetch hook (`useCreatorMetricRowsFetcher`) so that
// `queryClient.fetchQuery` can deduplicate against already-mounted queries.
// If you change a key here, also update the matching key builder in this file.
const creatorMetricsListKey = (
  merchant: string,
  workspaceId: string | null,
  params: CreatorMetricsFilters & CreatorMetricsRange,
  pagination: Pagination,
  sort: CreatorMetricsSort,
) =>
  [
    "creator-overview",
    merchant,
    workspaceId,
    params.range?.start?.toString(),
    params.range?.end?.toString(),
    pagination.limit,
    pagination.offset,
    params.text,
    sort.column,
    sort.direction,
    params.riskTier?.toString(),
    params.riskId?.toString(),
    params.videoRiskStatus,
    params.minVideosAtRiskPercent,
    params.maxVideosAtRiskPercent,
    params.creatorList,
    params.creatorListIds?.toString(),
    params.creatorClassifications?.toString(),
    params.includeTagIds?.toString(),
    params.excludeTagIds?.toString(),
    params.includeCohortIds?.toString(),
    params.excludeCohortIds?.toString(),
    params.creatorType,
    params.maxGmv,
    params.minGmv,
    params.productIds?.toString(),
    params.minCtr,
    params.maxCtr,
    params.minCtor,
    params.maxCtor,
    params.minOrders,
    params.maxOrders,
    params.minAov,
    params.maxAov,
    params.classification,
    params.maleAudiencePercent,
    params.femaleAudiencePercent,
    params.age?.toString(),
    params.toneAndApproach?.toString(),
    params.contentFocus?.toString(),
    params.productionQuality?.toString(),
    params.topSellingCategories?.toString(),
    params.minFollowers,
    params.maxFollowers,
    params.minTotalLikes,
    params.maxTotalLikes,
    params.minPostingFrequency,
    params.maxPostingFrequency,
    params.collaborationType,
    params.campaignIds?.toString(),
    params.creatorLevel?.toString(),
    params.categoryPro?.toString(),
    params.creatorLanguage?.toString(),
    params.postedFrom,
    params.postedTo,
    params.ruleArea,
    params.ruleId,
    params.not?.toString(),
  ] as const;

const creatorsCountKey = (
  merchant: string,
  workspaceId: string | null,
  params: CreatorMetricsFilters & CreatorMetricsRange,
) =>
  [
    "creators-count",
    merchant,
    workspaceId,
    params.range?.start?.toString(),
    params.range?.end?.toString(),
    params.text,
    params.riskTier?.toString(),
    params.riskId?.toString(),
    params.videoRiskStatus,
    params.minVideosAtRiskPercent,
    params.maxVideosAtRiskPercent,
    params.creatorList,
    params.creatorListIds?.toString(),
    params.creatorClassifications?.toString(),
    params.includeTagIds?.toString(),
    params.excludeTagIds?.toString(),
    params.includeCohortIds?.toString(),
    params.excludeCohortIds?.toString(),
    params.creatorType,
    params.maxGmv,
    params.minGmv,
    params.productIds?.toString(),
    params.minCtr,
    params.maxCtr,
    params.minCtor,
    params.maxCtor,
    params.minOrders,
    params.maxOrders,
    params.minAov,
    params.maxAov,
    params.classification,
    params.maleAudiencePercent,
    params.femaleAudiencePercent,
    params.age?.toString(),
    params.toneAndApproach?.toString(),
    params.contentFocus?.toString(),
    params.productionQuality?.toString(),
    params.topSellingCategories?.toString(),
    params.minFollowers,
    params.maxFollowers,
    params.minTotalLikes,
    params.maxTotalLikes,
    params.minPostingFrequency,
    params.maxPostingFrequency,
    params.collaborationType,
    params.campaignIds?.toString(),
    params.creatorLevel?.toString(),
    params.categoryPro?.toString(),
    params.creatorLanguage?.toString(),
    params.postedFrom,
    params.postedTo,
    params.ruleArea,
    params.ruleId,
    params.not?.toString(),
  ] as const;

const fetchCreatorMetricsList = async (
  merchant: string,
  workspaceId: string | null,
  params: CreatorMetricsFilters & CreatorMetricsRange,
  pagination: { limit: number; offset: number },
  sort?: CreatorMetricsSort,
) => {
  const scope = getScopedMerchantClient(merchant, workspaceId);
  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].explorer.creators.$get({
        param: scope.param,
        query: {
          ...buildCreatorMetricsApiQuery(params),
          limit: pagination.limit.toString(),
          offset: pagination.offset.toString(),
          ...(sort ? { column: sort.column, direction: sort.direction } : {}),
        },
      })
    : await client.merchant[":merchantId"].explorer.creators.$get({
        param: scope.param,
        query: {
          ...buildCreatorMetricsApiQuery(params),
          limit: pagination.limit.toString(),
          offset: pagination.offset.toString(),
          ...(sort ? { column: sort.column, direction: sort.direction } : {}),
        },
      });
  if (!res.ok) throw await handleError(res);
  return await res.json();
};

const fetchCreatorsCount = async (
  merchant: string,
  workspaceId: string | null,
  params: CreatorMetricsFilters & CreatorMetricsRange,
) => {
  const scope = getScopedMerchantClient(merchant, workspaceId);
  const res = scope.workspaceId
    ? await client.workspace[":workspaceId"].merchant[":merchantId"].explorer.creators.count.$get({
        param: scope.param,
        query: buildCreatorMetricsApiQuery(params),
      })
    : await client.merchant[":merchantId"].explorer.creators.count.$get({
        param: scope.param,
        query: buildCreatorMetricsApiQuery(params),
      });
  if (!res.ok) throw await handleError(res);
  return await res.json();
};

export const useCreatorsMetrics = ({
  pagination,
  sort,
  enabled = true,
  ...params
}: CreatorMetricsRange & {
  pagination: Pagination;
  sort: CreatorMetricsSort;
  enabled?: boolean;
} & CreatorMetricsFilters) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: creatorMetricsListKey(scope.merchantId, scope.workspaceId, params, pagination, sort),
    queryFn: () => fetchCreatorMetricsList(scope.merchantId, scope.workspaceId, params, pagination, sort),
    enabled: enabled && !!scope.merchantId && !!params.range?.start && !!params.range?.end,
  });
};

export const useCreatorsCount = ({
  enabled = true,
  ...params
}: CreatorMetricsRange & { enabled?: boolean } & CreatorMetricsFilters) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: creatorsCountKey(scope.merchantId, scope.workspaceId, params),
    queryFn: () => fetchCreatorsCount(scope.merchantId, scope.workspaceId, params),
    enabled: enabled && !!scope.merchantId && !!params.range?.start && !!params.range?.end,
  });
};

export const useCreatorMetricRowsFetcher = (params: CreatorMetricsFilters & CreatorMetricsRange) => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useCallback(
    async (offset: number, limit: number): Promise<ChunkResponse<CreatorMetrics>> => {
      const pagination = { limit, offset };
      const fetcherSort: CreatorMetricsSort = { column: "gmv", direction: "desc" };

      const [listData, countData] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: [...creatorMetricsListKey(scope.merchantId, scope.workspaceId, params, pagination, fetcherSort)],
          queryFn: () => fetchCreatorMetricsList(scope.merchantId, scope.workspaceId, params, pagination, fetcherSort),
          staleTime: Number.POSITIVE_INFINITY,
        }),
        queryClient.fetchQuery({
          queryKey: [...creatorsCountKey(scope.merchantId, scope.workspaceId, params)],
          queryFn: () => fetchCreatorsCount(scope.merchantId, scope.workspaceId, params),
          staleTime: Number.POSITIVE_INFINITY,
        }),
      ]);

      return { items: listData.creators, total: countData.totalCreators };
    },
    [queryClient, scope.merchantId, scope.workspaceId, params],
  );
};

const CHUNK_SIZE = 200;
export const useCreatorMetricsDownload = () => {
  const scope = useScopedMerchantClient();

  return useMutation<
    CreatorMetrics[],
    void,
    {
      total: number;
      sort: CreatorMetricsSort;
    } & CreatorMetricsRange &
      CreatorMetricsFilters
  >({
    mutationFn: async ({ total, sort, ...params }) => {
      const chunks = [...Array(Math.ceil(total / CHUNK_SIZE)).keys()];

      const requests = chunks.map((page) =>
        fetchCreatorMetricsList(
          scope.merchantId,
          scope.workspaceId,
          params,
          { limit: CHUNK_SIZE, offset: CHUNK_SIZE * page },
          sort,
        ),
      );

      try {
        const response = await Promise.all(requests);
        return response.flatMap((res) => res.creators);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
  });
};
